import { useQuery } from '@tanstack/react-query';

export type McpServerInfo =
  | { enabled: false }
  | { enabled: true; resource: string; scopes: string[] };

export const mcpServerInfoQueryKey = ['mcp', 'server-info'] as const;

const METADATA_PATH = '/.well-known/oauth-protected-resource/mcp';

/**
 * Drop a body we will not read. A response whose body is never consumed stays
 * "in flight" for the browser, so `networkidle` never fires on any account page
 * (the SPA-fallback HTML answer is exactly such a body).
 */
async function discardBody(response: Response): Promise<void> {
  try {
    await response.body?.cancel();
  } catch {
    // Nothing left to release.
  }
}

async function fetchMcpServerInfo(): Promise<McpServerInfo> {
  // Same origin as the portal, and not part of the typed API schema.
  // `no-cache` makes the browser revalidate: the document is served with
  // `max-age=3600`, which would otherwise outlive the query's staleTime.
  const response = await fetch(METADATA_PATH, {
    headers: { accept: 'application/json' },
    cache: 'no-cache',
  });
  // With MCP off no route exists for this path. In a deployment that serves the
  // portal, the SPA fallback then answers 200 with index.html, so a 404 is not
  // the only "off" shape: any body that is not JSON means the same thing.
  if (response.status === 404) {
    await discardBody(response);
    return { enabled: false };
  }
  if (!response.ok) {
    await discardBody(response);
    throw new Error('mcp_server_info_failed');
  }
  if (!(response.headers.get('content-type') ?? '').includes('json')) {
    await discardBody(response);
    return { enabled: false };
  }
  let body: { resource?: unknown; scopes_supported?: unknown };
  try {
    body = (await response.json()) as typeof body;
  } catch (err) {
    // A body that does not parse means this is not the metadata document. A
    // read that failed mid-stream (a TypeError) says nothing about MCP, so let
    // the query report an error instead of caching "off".
    if (err instanceof SyntaxError) return { enabled: false };
    throw err;
  }
  if (body === null || typeof body !== 'object') return { enabled: false };
  if (typeof body.resource !== 'string' || body.resource === '') return { enabled: false };
  const scopes = Array.isArray(body.scopes_supported)
    ? body.scopes_supported.filter((s): s is string => typeof s === 'string')
    : [];
  return { enabled: true, resource: body.resource, scopes };
}

/** Whether this deployment serves MCP, and the URL a client should be given. */
export function useMcpServerInfo() {
  return useQuery<McpServerInfo, Error>({
    queryKey: mcpServerInfoQueryKey,
    queryFn: fetchMcpServerInfo,
    staleTime: 5 * 60_000,
    retry: false,
  });
}
